import i18n from "i18next";
/**
 * PdfPreview — Live PDF preview panel.
 *
 * Fetches the compiled PDF from the API export endpoint and renders it
 * in an <iframe> using a blob URL. Supports page navigation, zoom,
 * loading/error states, and auto-refresh after document saves.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  IconAlertTriangle,
  IconLoader2,
  IconDownload,
  IconBolt,
  IconBoltOff,
} from "@tabler/icons-react";
import { Button } from "@/components/lilia-ui/button";
import { RefreshButton } from "@/components/shared/refreshButton/RefreshButton";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import { recordPreviewTiming, type PreviewTrigger } from "@/lib/preview-timings";
import {
  COMPILE_TICKET_HEADER,
  MAX_BUSY_RETRIES,
  busyRetryAfter,
  newCompileTicket,
  useCompilePlace,
  waitFor,
} from "./compilePlace";

type PdfStatus = "idle" | "loading" | "ready" | "error";

/**
 * How long the document has to be still before a recompile is worth starting.
 *
 * Auto-refresh was switched off on 2026-05-14 for "burning typst compile
 * cycles" and feeling jittery. Both causes are addressed rather than assumed
 * away: the server now caches on a hash of the generated source, so a refresh
 * that changes nothing costs a 304, and this pause means a burst of typing is
 * one compile instead of one per keystroke. 800ms is long enough to sit
 * between sentences, short enough that a deliberate pause feels answered.
 */
const AUTO_REFRESH_DEBOUNCE_MS = 800;

const AUTO_REFRESH_KEY = "lilia.preview-auto-refresh";

/** Default on, per the decision to try it again — but the toggle is sticky. */
function readAutoRefreshPreference(): boolean {
  try {
    return localStorage.getItem(AUTO_REFRESH_KEY) !== "off";
  } catch {
    return true;
  }
}

interface PdfPreviewProps {
  documentId: string;
  /** Increment to trigger a re-fetch (e.g. after save). */
  refreshKey?: number;
  /**
   * Changes whenever the document's content changes. Auto-refresh keys off
   * this rather than off the blocks array, so a re-render that produces an
   * identical document does not trigger a compile.
   */
  contentSignature?: string;
  /** Told when a compile starts and ends, for a host's own refresh control. */
  onBusyChange?: (busy: boolean) => void;
}

export function PdfPreview({ documentId, refreshKey = 0, contentSignature, onBusyChange }: PdfPreviewProps) {
  const { fetchWithAuth } = useApiContext();

  const [status, setStatus] = useState<PdfStatus>("idle");
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [renderEngine, setRenderEngine] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(readAutoRefreshPreference);
  /** When the PDF on screen was produced — the refresh button's timestamp. */
  const [renderedAt, setRenderedAt] = useState<Date | null>(null);
  /** The ticket of the compile being waited for, and where it stands (compilePlace.ts). */
  const [ticket, setTicket] = useState<string | null>(null);
  /** Every compiler was busy; the preview is waiting to ask again. */
  const [busy, setBusy] = useState(false);
  const place = useCompilePlace(ticket, fetchWithAuth);

  // Track the current blob URL for cleanup
  const blobUrlRef = useRef<string | null>(null);

  // Abort controller for in-flight requests
  const abortRef = useRef<AbortController | null>(null);

  /** Resolves true when a PDF arrived, false when the compile failed, and
   *  undefined when a newer request superseded it. */
  const fetchPdf = useCallback(async (trigger: PreviewTrigger = "manual"): Promise<boolean | undefined> => {
    // Abort any previous in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus("loading");
    setErrorMessage(null);

    const startedAt = performance.now();
    const compileTicket = newCompileTicket();
    setTicket(compileTicket);

    try {
      // A busy server (every compiler taken for a minute) says when to ask
      // again; the preview waits and asks, a few times, before it shows an error.
      let response: Response;
      for (let attempt = 0; ; attempt++) {
        response = await fetchWithAuth(
          `${API_BASE}/documents/${documentId}/export/pdf`,
          { signal: controller.signal, headers: { [COMPILE_TICKET_HEADER]: compileTicket } }
        );
        const retryAfter = await busyRetryAfter(response);
        if (retryAfter === null || attempt >= MAX_BUSY_RETRIES) break;
        setBusy(true);
        await waitFor(retryAfter * 1000, controller.signal);
      }
      if (abortRef.current === controller) setBusy(false);

      if (!response.ok) {
        let message = `PDF compilation failed (${response.status})`;
        try {
          const body = await response.json();
          if (body.message) message = body.message;
          if (body.error) message = body.error;
          // Still busy after every retry: the sentence says what is happening.
          if (body.error === "compile_busy" && body.message) message = body.message;
        } catch {
          // Response body wasn't JSON — use the status-based message
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      // Which engine actually produced this PDF. The server picks it: the
      // default is "auto", which tries Typst and falls back to pdflatex the
      // moment it hits a translation gap.
      //
      // That fallback used to be invisible — this value existed only as a
      // `data-engine` attribute for E2E. It matters because the fallback is
      // half of a compound failure: Typst gap → silently becomes pdflatex →
      // pdflatex is the one engine that cannot render CJK → the characters are
      // dropped at exit code 0. Showing the engine is what makes the first
      // degradation visible, so the second is explicable rather than baffling.
      const engine = response.headers.get("X-Render-Engine");

      // Temporary instrumentation, kept local to this machine. Auto-refresh
      // comes back on the claim that it is now near-instant; this is what will
      // settle that with a distribution instead of an impression. Recorded
      // before the blob URL work so the number is the round trip, not the
      // render. See src/lib/preview-timings.ts.
      recordPreviewTiming({
        at: new Date().toISOString(),
        ms: Math.round(performance.now() - startedAt),
        trigger,
        engine,
        cache: response.headers.get("X-Typst-Cache"),
        bytes: blob.size,
      });

      // Clean up old blob URL
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }

      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setBlobUrl(url);
      setRenderEngine(engine);
      setRenderedAt(new Date());
      setStatus("ready");
      return true;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return undefined; // Request was aborted — ignore
      }
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Failed to load PDF");
      return false;
    } finally {
      // Only the newest request owns the ticket and the busy notice.
      if (abortRef.current === controller) {
        setTicket(null);
        setBusy(false);
      }
    }
  }, [documentId, fetchWithAuth]);

  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  useEffect(() => {
    onBusyChangeRef.current?.(status === "loading");
  }, [status]);

  /** The manual refresh: rejects when the compile fails, so the button can
   *  say so. A request superseded by a newer one is not a failure. */
  const refreshManually = useCallback(async () => {
    const ok = await fetchPdf("manual");
    if (ok === false) throw new Error("PDF compilation failed");
  }, [fetchPdf]);

  // Fetch on mount and when refreshKey changes
  const hasMountedRef = useRef(false);
  useEffect(() => {
    fetchPdf(hasMountedRef.current ? "manual" : "mount");
    hasMountedRef.current = true;
  }, [fetchPdf, refreshKey]);

  // Auto-refresh, debounced.
  //
  // Seeded with the signature present at mount so arriving on the tab does not
  // count as an edit — the mount effect above has already fetched. Only a
  // change from what was last rendered starts the timer, and each further
  // change restarts it, so a burst of typing costs one compile.
  const lastRenderedSignatureRef = useRef<string | undefined>(contentSignature);
  useEffect(() => {
    if (!autoRefresh) {
      // Stay in step while disabled, so re-enabling does not immediately fire
      // for edits made in the meantime. The Refresh button is right there.
      lastRenderedSignatureRef.current = contentSignature;
      return;
    }
    if (contentSignature === undefined) return;
    if (contentSignature === lastRenderedSignatureRef.current) return;

    const timer = setTimeout(() => {
      lastRenderedSignatureRef.current = contentSignature;
      fetchPdf("auto");
    }, AUTO_REFRESH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [contentSignature, autoRefresh, fetchPdf]);

  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh((on) => {
      const next = !on;
      try {
        localStorage.setItem(AUTO_REFRESH_KEY, next ? "on" : "off");
      } catch {
        // Preference is not worth failing over; it just will not stick.
      }
      return next;
    });
  }, []);

  // Cleanup blob URL and abort on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `document-${documentId}.pdf`;
    a.click();
  }, [blobUrl, documentId]);

  // How the wait reads: busy and trying again, a place in line, or the usual.
  const waitText = busy
    ? i18n.t("editor:pdf.busyRetrying")
    : place !== null && place > 0
      ? i18n.t("editor:pdf.placeInLine", { place })
      : i18n.t("editor:pdf.thisMayTakeAFew");

  // Loading state
  if (status === "loading" && !blobUrl) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[var(--lilia-text-muted)]">
        <IconLoader2 size={28} className="animate-spin" />
        <span className="text-sm font-medium">{i18n.t("editor:pdf.compilingPdf")}</span>
        <span className="text-xs" data-testid="pdf-compile-wait">{waitText}</span>
      </div>
    );
  }

  // Error state (no previous PDF to show)
  if (status === "error" && !blobUrl) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
        <IconAlertTriangle size={28} className="text-red-500" />
        <span className="text-sm font-medium text-[var(--lilia-text)]">{i18n.t("editor:pdf.pdfCompilationFailed")}</span>
        <span className="text-xs text-[var(--lilia-text-muted)] max-w-[320px]">{errorMessage}</span>
        <Button data-testid="pdf-retry-button" variant="subtle" size="sm" onClick={() => fetchPdf("manual")} className="mt-2">
          {i18n.t("editor:pdf.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col min-h-0 overflow-hidden"
      data-engine={renderEngine ?? undefined}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-end gap-1 px-3 py-1.5 border-b border-[var(--lilia-border)] bg-[var(--lilia-surface)]">
        {status === "loading" && (
          <span className="flex items-center gap-1 text-xs text-[var(--lilia-text-muted)]">
            <IconLoader2 size={12} className="animate-spin" />
            <span data-testid="pdf-compile-wait">
              {busy || (place !== null && place > 0) ? waitText : i18n.t("editor:pdf.updating")}
            </span>
          </span>
        )}
        {status === "error" && blobUrl && (
          <span className="flex items-center gap-1 text-xs text-red-500" title={errorMessage || undefined}>
            <IconAlertTriangle size={12} />
            {i18n.t("editor:pdf.updateFailed")}
          </span>
        )}
        {/* Which engine produced this PDF.
            Quiet by design — most of the time it is a detail the author has no
            reason to care about, and they never chose an engine in the first
            place. It earns its place on the days it disagrees with what they
            expected: the default "auto" tries Typst and drops to pdflatex on
            any translation gap, and pdflatex is the engine that cannot render
            CJK. A gap is acceptable; being unable to find out is not. */}
        {status === "ready" && renderEngine && (
          <span
            data-testid="pdf-render-engine"
            className="mr-1 text-xs text-[var(--lilia-text-muted)]"
            title={`This PDF was produced by ${renderEngine}. The engine is chosen automatically, and can change between renders if a document uses something the preferred engine cannot express.`}
          >
            {renderEngine}
          </span>
        )}
        {/* Auto-refresh, and the ability to turn it off.
            It was off entirely from 2026-05-14 because it burned compile
            cycles and felt jittery. Both causes are addressed — the server
            caches on a hash of the generated source, and edits are debounced —
            but "addressed" is a claim about feel, so the switch stays where
            the person doing the authoring can reach it. */}
        <Button
          data-testid="pdf-auto-refresh-toggle"
          variant="subtle"
          size="icon-sm"
          onClick={toggleAutoRefresh}
          aria-pressed={autoRefresh}
          title={
            autoRefresh
              ? "Auto-refresh is on — the PDF updates shortly after you stop typing. Click to turn off."
              : "Auto-refresh is off — use Refresh to recompile. Click to turn on."
          }
        >
          {autoRefresh ? <IconBolt size={14} /> : <IconBoltOff size={14} />}
        </Button>
        {/* Manual refresh — still the way to force a recompile, and the only
            way when auto-refresh is off. */}
        {/* One RefreshButton everywhere (saved-moment handoff §5). It stays
            enabled while compiling: pressing again restarts the compile. The
            timestamp says how fresh the PDF on screen is. */}
        <RefreshButton
          testId="pdf-refresh-button"
          label={i18n.t("editor:pdf.refreshPdf")}
          onRefresh={refreshManually}
          subject="PDF"
          updatedAt={renderedAt}
          iconSize={14}
        />
        <Button variant="subtle" size="icon-sm" onClick={handleDownload} title={i18n.t("editor:pdf.downloadPdf")}>
          <IconDownload size={14} />
        </Button>
      </div>

      {/* PDF iframe — relative container + absolute iframe gives reliable height in any flex context */}
      <div className="flex-1 overflow-hidden bg-[#525659] relative">
        {blobUrl && (
          <iframe
            key={blobUrl}
            src={blobUrl}
            title={i18n.t("editor:pdf.pdfPreview")}
            className="absolute inset-0 border-0 w-full h-full"
          />
        )}
      </div>
    </div>
  );
}
